import Compressor from 'compressorjs'

/** Matches ANAPI `GenerateEventMediaUploadPost::MAX_UPLOAD_BYTES` (8 MB). */
export const ANAPI_MAX_UPLOAD_BYTES = 8 * 1024 * 1024

/** Longest edge (width or height) after resize — tuned for mobile web + hosted apps (e.g. trivia photos). */
export const DEFAULT_WEB_IMAGE_MAX_EDGE_PX = 640

export const DEFAULT_PREPARE_IMAGE_OPTIONS = {
  maxSourceMb: 20,
  maxEdgePx: DEFAULT_WEB_IMAGE_MAX_EDGE_PX,
  passthroughMaxKb: 500,
  webpQuality: 0.9,
  imageOptimization: true,
  maxUploadBytes: ANAPI_MAX_UPLOAD_BYTES,
  /**
   * Size the quality loop aims for. Defaults to the hard API cap so existing callers keep
   * their current behavior; camera capture passes a smaller adaptive budget so a photo
   * taken on venue wifi finishes uploading.
   */
  targetBytes: 0
}

/**
 * Sync check whether canvas can encode WebP (Safari < 14 may return false).
 */
export function browserSupportsWebpCanvasEncode() {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    const dataUrl = canvas.toDataURL('image/webp')
    return typeof dataUrl === 'string' && dataUrl.indexOf('data:image/webp') === 0
  } catch {
    return false
  }
}

function stripExtension(name) {
  if (!name || typeof name !== 'string') return ''
  return name.replace(/\.[^/.]+$/, '')
}

function loadImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image dimensions.'))
    }
    img.src = url
  })
}

function compressWithCompressor(file, {
  maxWidth,
  maxHeight,
  mimeType,
  quality,
  checkOrientation
}) {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line no-new
    new Compressor(file, {
      maxWidth,
      maxHeight,
      mimeType,
      quality,
      checkOrientation,
      success: resolve,
      error: reject
    })
  })
}

/**
 * @param {File} file
 * @param {object} [options]
 * @returns {Promise<{ file: File, didProcess: boolean, displayName: string }>}
 */
export async function prepareEventImageForUpload(file, options = {}) {
  const opts = { ...DEFAULT_PREPARE_IMAGE_OPTIONS, ...options }
  const maxSourceBytes = opts.maxSourceMb * 1024 * 1024

  if (file.size > maxSourceBytes) {
    throw new Error(`File is too large (max ${opts.maxSourceMb} MB before processing).`)
  }

  if (!opts.imageOptimization) {
    return { file, didProcess: false, displayName: file.name }
  }

  if (!file.type || !file.type.startsWith('image/')) {
    return { file, didProcess: false, displayName: file.name }
  }

  if (file.type === 'image/gif') {
    return { file, didProcess: false, displayName: file.name }
  }

  const passthroughMaxBytes = opts.passthroughMaxKb * 1024
  const maxEdge = opts.maxEdgePx

  if (file.type === 'image/webp') {
    const dims = await loadImageDimensions(file)
    const longest = Math.max(dims.width, dims.height)
    if (longest <= maxEdge && file.size <= passthroughMaxBytes) {
      return { file, didProcess: false, displayName: file.name }
    }
  }

  const useWebp = browserSupportsWebpCanvasEncode()
  const mimeType = useWebp ? 'image/webp' : 'image/jpeg'
  const ext = useWebp ? '.webp' : '.jpg'

  const largeSource = file.size > 10 * 1024 * 1024
  const checkOrientation = !largeSource

  const hardCap = opts.maxUploadBytes > 0 ? opts.maxUploadBytes : ANAPI_MAX_UPLOAD_BYTES
  const targetBytes = opts.targetBytes > 0 ? Math.min(opts.targetBytes, hardCap) : hardCap

  let quality = opts.webpQuality
  let blob = null
  for (let attempt = 0; attempt < 6; attempt++) {
    blob = await compressWithCompressor(file, {
      maxWidth: maxEdge,
      maxHeight: maxEdge,
      mimeType,
      quality,
      checkOrientation
    })
    if (blob.size <= targetBytes) break
    quality *= 0.82
  }

  if (blob.size > hardCap) {
    throw new Error('Processed image still exceeds the upload size limit. Try a smaller source image.')
  }

  const baseName = stripExtension(file.name)
  const outName = `${baseName || 'image'}${ext}`
  const outFile = new File([blob], outName, { type: mimeType })

  return { file: outFile, didProcess: true, displayName: outName }
}
